import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, Polyline, useMap } from 'react-leaflet';
import type { LatLngExpression, LatLngBoundsExpression } from 'leaflet';
import L from 'leaflet';
import FullscreenWrapper from './common/FullscreenWrapper';
import PlatformIcon from './common/PlatformIcon';

export type NearbySpot = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  description?: string;
  imageUrl?: string;
  address?: string;
  category?: string;
  source?: 'card' | 'osm';
  website?: string;
  phone?: string;
  opening_hours?: string;
  tags?: Record<string, any>;  // Changed from Record<string, string> to Record<string, any> to support all OSM tag value types
};

const RecenterOnChange: React.FC<{ 
  center: LatLngExpression; 
  bounds?: LatLngBoundsExpression | null;
  searchedLocation?: { lat: number; lon: number } | null;
}> = ({ center, bounds, searchedLocation }) => {
  const map = useMap();
  
  useEffect(() => {
    if (searchedLocation) {
      // If there's a searched location, jump to it
      map.setView([searchedLocation.lat, searchedLocation.lon], 15, { animate: true });
    } else if (bounds) {
      map.fitBounds(bounds, { padding: [32, 32] });
    } else {
      map.setView(center);
    }
  }, [center, bounds, map, searchedLocation]);
  
  return null;
};

// Component to render route lines with distance labels
const RouteLines: React.FC<{ spots: NearbySpot[]; showLabels?: boolean }> = ({ spots, showLabels = true }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!map || spots.length < 2) return;
    if (!showLabels) return; // Skip adding distance labels when disabled
    
    const markers: L.Marker[] = [];
    
    // Draw lines and distance labels between consecutive spots
    for (let i = 1; i < spots.length; i++) {
      const prev = spots[i - 1];
      const curr = spots[i];
      const distance = calculateDistance(prev.lat, prev.lng, curr.lat, curr.lng);
      const distanceText = formatDistance(distance);
      
      // Calculate midpoint for label
      const midLat = (prev.lat + curr.lat) / 2;
      const midLng = (prev.lng + curr.lng) / 2;
      
      // Create distance label as a DivIcon
      const labelIcon = L.divIcon({
        className: '',
        html: `
          <div style="
            background: rgba(255, 255, 255, 0.95);
            border: 2px solid #0B61D6;
            border-radius: 12px;
            padding: 4px 10px;
            font-size: 12px;
            font-weight: 700;
            color: #0B61D6;
            box-shadow: 0 2px 8px rgba(11, 97, 214, 0.25);
            white-space: nowrap;
            pointer-events: none;
            line-height: 1.2;
            text-align: center;
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
          ">
            ${distanceText}
          </div>
        `,
        iconSize: [1, 1],
        iconAnchor: [0.5, 0.5],
      });
      
      const marker = L.marker([midLat, midLng], { icon: labelIcon }).addTo(map);
      markers.push(marker);
    }
    
    // Cleanup on unmount
    return () => {
      markers.forEach(marker => marker.remove());
    };
  }, [map, spots, showLabels]);
  
  return null;
};

const MapMoveHandler: React.FC<{
  onMoveEnd: (lat: number, lon: number) => void;
  onZoomEnd: (zoom: number) => void;
}> = ({ onMoveEnd, onZoomEnd }) => {
  const map = useMap();
  
  useEffect(() => {
    let moveTimeout: NodeJS.Timeout;
    
    const handleMoveEnd = () => {
      // Debounce: wait 1 second after user stops moving the map
      clearTimeout(moveTimeout);
      moveTimeout = setTimeout(() => {
        const center = map.getCenter();
        onMoveEnd(center.lat, center.lng);
      }, 1000);
    };
    
    const handleZoomEnd = () => {
      const zoom = map.getZoom();
      onZoomEnd(zoom);
    };
    
    map.on('moveend', handleMoveEnd);
    map.on('zoomend', handleZoomEnd);
    
    return () => {
      clearTimeout(moveTimeout);
      map.off('moveend', handleMoveEnd);
      map.off('zoomend', handleZoomEnd);
    };
  }, [map, onMoveEnd, onZoomEnd]);
  
  return null;
};

// Haversine 公式计算两点之间的距离（单位：米）
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371e3; // 地球半径，单位：米
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // 返回距离，单位：米
};

// 格式化距离显示
const formatDistance = (meters: number): string => {
  if (meters < 1000) {
    return `${Math.round(meters)}米`;
  }
  return `${(meters / 1000).toFixed(1)}公里`;
};

const escapeHtmlAttr = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const safeLetter = (input?: string) => {
  if (!input) return '';
  const trimmed = input.trim();
  if (!trimmed) return '';
  const candidate = trimmed[0].toUpperCase();
  return escapeHtmlAttr(candidate);
};

const buildIcon = ({
  image,
  size,
  ringColor,
  fallbackLetter,
  highlight,
  iconName,
  category,
}: {
  image?: string;
  size: number;
  ringColor: string;
  fallbackLetter?: string;
  highlight?: boolean;
  iconName?: string;
  category?: string;
}) => {
  // DEBUG
  console.log('[buildIcon]', { image, size, ringColor, fallbackLetter, highlight });
  
  const dimension = `${size}px`;
  const highlightBorderColor = '#F97316';
  const ring = highlight ? highlightBorderColor : '#ffffff';
  const shadow = highlight
    ? '0 12px 28px rgba(249,115,22,0.35)'
    : `0 10px 22px rgba(17,24,39,0.22)`;

  let innerHtml = '';
  if (image) {
    const escapedSrc = escapeHtmlAttr(image);
    innerHtml = `<img src="${escapedSrc}" alt="" style="width:100%;height:100%;object-fit:cover;display:block;" />`;
  } else if (iconName) {
    // Use Material Icon
    const iconSize = Math.round(size * 0.55);
    innerHtml = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:${highlight ? '#FEB273' : ringColor};color:#fff;">
      <span class="material-icons" style="font-size:${iconSize}px;line-height:1;">${iconName}</span>
    </div>`;
  } else {
    const letter = safeLetter(fallbackLetter);
    innerHtml = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:${highlight ? '#FEB273' : ringColor};color:#fff;font-weight:700;font-size:${Math.round(size * 0.42)}px;">${letter}</div>`;
  }

  const indicator = highlight
    ? `<div style="position:absolute;bottom:-6px;right:-6px;width:${Math.round(size * 0.42)}px;height:${Math.round(size * 0.42)}px;border-radius:9999px;background:${highlightBorderColor};display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:${Math.max(12, Math.round(size * 0.28))}px;border:2px solid #fff;box-shadow:0 8px 18px rgba(249,115,22,0.35);">★</div>`
    : '';

  const html = `
    <div style="position:relative;width:${dimension};height:${dimension};display:flex;align-items:center;justify-content:center;">
      <div style="position:relative;width:100%;height:100%;border-radius:9999px;border:${highlight ? 4 : 3}px solid ${ring};overflow:hidden;box-shadow:${shadow};background:${ringColor};">
        ${innerHtml}
      </div>
      ${indicator}
    </div>
  `;

  return L.divIcon({ className: '', html, iconAnchor: [size / 2, size / 2] });
};

type FilterKey = 'poi' | 'restaurant' | 'shopping' | 'lodging' | 'other';

const FILTERS: Array<{
  key: FilterKey;
  label: string;
  icon: { name: string; family?: 'MaterialIcons' | 'AntDesign' };
}> = [
  { key: 'poi', label: '景点', icon: { name: 'attractions', family: 'MaterialIcons' } },
  { key: 'restaurant', label: '餐厅', icon: { name: 'restaurant', family: 'MaterialIcons' } },
  { key: 'shopping', label: '购物', icon: { name: 'shopping-bag', family: 'MaterialIcons' } },
  { key: 'lodging', label: '酒店', icon: { name: 'hotel', family: 'MaterialIcons' } },
];

const normalizeCategory = (raw?: string): FilterKey => {
  if (!raw) return 'other';
  const s = raw.toLowerCase();
  if (s.includes('restaurant') || s.includes('food') || s.includes('eat') || s.includes('cafe')) return 'restaurant';
  if (s.includes('hotel') || s.includes('lodging') || s.includes('stay') || s.includes('hostel') || s.includes('inn')) return 'lodging';
  if (s.includes('shop') || s.includes('shopping') || s.includes('store') || s.includes('market') || s.includes('mall')) return 'shopping';
  if (s.includes('poi') || s.includes('sight') || s.includes('attraction') || s.includes('scenic') || s.includes('landmark') || s.includes('museum') || s.includes('park') ) return 'poi';
  return 'other';
};

const getCategoryIcon = (category?: string): string => {
  const cat = normalizeCategory(category);
  switch (cat) {
    case 'poi':
      return 'attractions';
    case 'restaurant':
      return 'restaurant';
    case 'shopping':
      return 'shopping_bag';
    case 'lodging':
      return 'hotel';
    default:
      return 'place';
  }
};

// Inject spin animation CSS and Material Icons font once
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (!document.getElementById('nearby-map-spin-animation')) {
    const style = document.createElement('style');
    style.id = 'nearby-map-spin-animation';
    style.innerHTML = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }
  
  // Load Material Icons font if not already loaded
  if (!document.getElementById('material-icons-font')) {
    const link = document.createElement('link');
    link.id = 'material-icons-font';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
    document.head.appendChild(link);
  }
}

const NearbyMap: React.FC<{
  center: [number, number];
  spots: NearbySpot[];
  primaryLabel?: string;
  primaryImageUrl?: string;
  primaryName?: string;
  primaryAddress?: string;
  onSelectSpot?: (spotId: string) => void;
  onCreateCard?: (spot: NearbySpot) => void;
  initialRadiusMeters?: number;
  // When true, show the total-distance badge in fullscreen header. Default: false.
  showTotal?: boolean;
  // When false, disable all dynamic OSM / Overpass fetching inside map.
  enableOSM?: boolean;
  // When false, do not render inter-point distance labels on the map.
  // City pages should pass `showInterPointDistances={false}` to hide these labels.
  showInterPointDistances?: boolean;
  // Custom height for the inline map. Default: 320.
  height?: number | string;
  // Whether to show the fullscreen toggle button. Default: true.
  showFullscreenButton?: boolean;
  // Whether to show the search bar / controls. Default: true.
  showControls?: boolean;
  // When true, removes default border radius and shadow for use in list items
  embedded?: boolean;
}> = ({
  center,
  spots,
  primaryLabel,
  primaryImageUrl,
  primaryName,
  primaryAddress,
  onSelectSpot,
  onCreateCard,
  initialRadiusMeters,
  showTotal,
  // Default OSM dynamic POIs to OFF; parent components should enable explicitly.
  enableOSM = false,
  // By default keep showing inter-point distances to preserve current behaviour.
  showInterPointDistances = true,
  height = 320,
  showFullscreenButton = true,
  showControls = true,
  embedded = false,
}) => {
  if (typeof window === 'undefined') return null;
  const normalizedCenter: LatLngExpression = center;
  const hasSpots = spots.length > 0;
  const [expanded, setExpanded] = useState(false);
  const [sheetActive, setSheetActive] = useState(false);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{
    display_name: string;
    lat: string;
    lon: string;
    type: string;
    name?: string;
  }>>([]);
  const [searching, setSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchedLocation, setSearchedLocation] = useState<{
    lat: number;
    lon: number;
    name: string;
    address: string;
  } | null>(null);
  const [dynamicPOIs, setDynamicPOIs] = useState<NearbySpot[]>([]);
  const [loadingPOIs, setLoadingPOIs] = useState(false);
  const [currentMapCenter, setCurrentMapCenter] = useState<[number, number]>(center);
  const [currentZoom, setCurrentZoom] = useState(15);
  // Track which spot is selected to show distance line from primary marker
  const [selectedSpotId, setSelectedSpotId] = useState<string | null>(null);
  // Track viewport width to hide controls on narrow screens in inline mode
  const [viewportWidth, setViewportWidth] = useState<number>(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));
  
  // Cache for loaded POI areas - stores center points of already fetched areas
  const loadedAreasRef = useRef<Array<{ lat: number; lon: number; radius: number }>>([]);
  const poiCacheRef = useRef<Map<string, NearbySpot>>(new Map());
  
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Check if a location is already covered by loaded areas
  const isAreaLoaded = useCallback((lat: number, lon: number, radiusMeters: number): boolean => {
    return loadedAreasRef.current.some(area => {
      const distance = calculateDistance(area.lat, area.lon, lat, lon);
      // Consider area loaded if new center is within 50% of the radius from a cached center
      return distance < area.radius * 0.5;
    });
  }, []);

  // Fetch nearby POIs using Overpass API
  const fetchNearbyPOIs = useCallback(async (lat: number, lon: number, radiusMeters: number = 1000) => {
    console.log('[NearbyMap] fetchNearbyPOIs called:', { lat, lon, radiusMeters, enableOSM });
    if (!enableOSM) {
      console.log('[NearbyMap] OSM disabled in fetchNearbyPOIs');
      return;
    }
    
    // Check if this area is already loaded
    if (isAreaLoaded(lat, lon, radiusMeters)) {
      console.log('[NearbyMap] Area already loaded, skipping fetch');
      return;
    }
    
    setLoadingPOIs(true);
    try {
      // Overpass query to get POIs around the location
      const query = `
        [out:json][timeout:25];
        (
          node["tourism"](around:${radiusMeters},${lat},${lon});
          node["amenity"="restaurant"](around:${radiusMeters},${lat},${lon});
          node["amenity"="cafe"](around:${radiusMeters},${lat},${lon});
          node["shop"](around:${radiusMeters},${lat},${lon});
          node["amenity"="hotel"](around:${radiusMeters},${lat},${lon});
          node["historic"](around:${radiusMeters},${lat},${lon});
          node["leisure"](around:${radiusMeters},${lat},${lon});
        );
        out body;
      `;
      
      const response = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        body: query,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch POIs');

      const data = await response.json();
      
      // Convert to NearbySpot format
      const newPOIs: NearbySpot[] = data.elements
        .filter((el: any) => el.tags?.name) // Only include POIs with names
        .map((el: any) => {
          let category = 'other';
          const tags = el.tags || {};
          
          if (tags.tourism || tags.historic || tags.leisure === 'park') {
            category = 'poi';
          } else if (tags.amenity === 'restaurant' || tags.amenity === 'cafe' || tags.amenity === 'bar') {
            category = 'restaurant';
          } else if (tags.shop || tags.amenity === 'marketplace') {
            category = 'shopping';
          } else if (tags.amenity === 'hotel' || tags.tourism === 'hotel' || tags.tourism === 'hostel') {
            category = 'lodging';
          }
          
          return {
            id: `osm-${el.id}`,
            name: tags.name || 'Unknown',
            lat: el.lat,
            lng: el.lon,
            description: tags.description || tags.tourism || tags.amenity || tags.shop,
            address: [tags['addr:street'], tags['addr:housenumber'], tags['addr:city']]
              .filter(Boolean)
              .join(', '),
            category,
            source: 'osm' as const,
            website: (tags.website || tags['contact:website'] || tags.url || undefined),
            phone: (tags.phone || tags['contact:phone'] || tags['contact:tel'] || tags.tel || undefined),
            opening_hours: (tags.opening_hours || undefined),
            tags: typeof tags === 'object' ? tags : undefined,
          };
        });

      console.log('[NearbyMap] Fetched POIs:', { count: newPOIs.length, sample: newPOIs.slice(0, 3) });
      
      // Add new POIs to cache
      newPOIs.forEach(poi => {
        poiCacheRef.current.set(poi.id, poi);
      });
      
      // Mark this area as loaded
      loadedAreasRef.current.push({ lat, lon, radius: radiusMeters });
      
      // Update state with all cached POIs (limited to 300 total)
      const allCachedPOIs = Array.from(poiCacheRef.current.values()).slice(0, 300);
      setDynamicPOIs(allCachedPOIs);
      
      console.log('[NearbyMap] Total cached POIs:', allCachedPOIs.length);
    } catch (error) {
      console.error('[NearbyMap] Error fetching POIs:', error);
    } finally {
      setLoadingPOIs(false);
    }
  }, [enableOSM, isAreaLoaded]);

  // React to OSM enable/disable changes.
  // - When disabled: clear caches and any dynamic POIs so the toggle actually removes OSM results.
  // - When enabled: trigger an initial fetch for the current center (unless user has a searched location).
  useEffect(() => {
    if (!enableOSM) {
      // Clear runtime caches/state related to OSM
      setDynamicPOIs([]);
      try { poiCacheRef.current.clear(); } catch (e) { /* ignore */ }
      try { loadedAreasRef.current = []; } catch (e) { /* ignore */ }
      setLoadingPOIs(false);
      return;
    }

    if (enableOSM && !searchedLocation) {
      fetchNearbyPOIs(center[0], center[1], initialRadiusMeters || 1000);
    }
  }, [enableOSM, center, initialRadiusMeters, searchedLocation, fetchNearbyPOIs]);

  // Search location using Nominatim API
  useEffect(() => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery || trimmedQuery.length < 3) {
      setSearchResults([]);
      setShowSuggestions(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?` +
          `q=${encodeURIComponent(trimmedQuery)}` +
          `&format=json&limit=8&addressdetails=1`,
          {
            headers: {
              'Accept': 'application/json',
              'User-Agent': 'CubeMeet/1.0'
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data || []);
          setShowSuggestions(data && data.length > 0);
        }
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 500); // Debounce 500ms

    return () => clearTimeout(timeoutId);
  }, [query]);
  const availableFilters = useMemo(() => {
    const present = new Set<FilterKey>();
    for (const s of spots) {
      present.add(normalizeCategory(s.category));
    }
    // Ensure four main filters always visible
    FILTERS.forEach(f => present.add(f.key));
    return present;
  }, [spots]);
  const [selected, setSelected] = useState<Set<FilterKey>>(() => new Set<FilterKey>(['poi','restaurant','shopping','lodging']));
  const inlineMapRef = useRef<L.Map | null>(null);
  const fullscreenMapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    return () => {
      inlineMapRef.current = null;
      fullscreenMapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!expanded) return;
    const frame = window.requestAnimationFrame(() => setSheetActive(true));
    return () => {
      window.cancelAnimationFrame(frame);
      setSheetActive(false);
    };
  }, [expanded]);

  const toggleExpanded = useCallback((event?: React.MouseEvent<HTMLButtonElement>) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setExpanded((prev) => !prev);
  }, []);

  const closeExpanded = useCallback(() => {
    setExpanded(false);
  }, []);

  const handleSearchResultClick = useCallback((result: typeof searchResults[0]) => {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);
    
    // Save the searched location to display a marker and trigger map update
    setSearchedLocation({
      lat,
      lon,
      name: result.name || result.display_name.split(',')[0],
      address: result.display_name,
    });
    
    // Update map center and fetch POIs for the new location
    setCurrentMapCenter([lat, lon]);
    if (enableOSM) {
      fetchNearbyPOIs(lat, lon, 1000);
    }
    
    // Clear search UI
    setQuery('');
    setShowSuggestions(false);
    setSearchResults([]);
  }, [fetchNearbyPOIs, enableOSM]);

  const handleMapMoveEnd = useCallback((lat: number, lon: number) => {
    console.log('[NearbyMap] handleMapMoveEnd called:', { lat, lon, enableOSM });
    if (!enableOSM) {
      console.log('[NearbyMap] OSM disabled, skipping POI fetch');
      return;
    }
    
    // Calculate distance from current center
    const distance = calculateDistance(currentMapCenter[0], currentMapCenter[1], lat, lon);
    console.log('[NearbyMap] Distance from current center:', distance, 'meters');
    
    // Only fetch if moved more than 500 meters
    if (distance < 500) {
      console.log('[NearbyMap] Distance too small, skipping fetch');
      return;
    }
    
    console.log('[NearbyMap] Fetching POIs for new location:', { lat, lon });
    setCurrentMapCenter([lat, lon]);
    fetchNearbyPOIs(lat, lon, 1000);
  }, [fetchNearbyPOIs, enableOSM, currentMapCenter]);

  const handleZoomEnd = useCallback((zoom: number) => {
    setCurrentZoom(zoom);
  }, []);

  useEffect(() => {
    if (!expanded) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (showSuggestions) {
          setShowSuggestions(false);
        } else {
          closeExpanded();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [expanded, closeExpanded, showSuggestions]);

  // Click outside to close suggestions
  useEffect(() => {
    if (!showSuggestions) return;
    const handler = () => setShowSuggestions(false);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [showSuggestions]);

  const computedBounds = useMemo<LatLngBoundsExpression | null>(() => {
    if (!initialRadiusMeters || initialRadiusMeters <= 0) return null;
    const [lat, lon] = center;
    const radius = initialRadiusMeters;
    const earthRadiusMeters = 6371000;
    const angular = radius / earthRadiusMeters;
    const latRadians = (lat * Math.PI) / 180;
    const minLat = lat - (angular * 180) / Math.PI;
    const maxLat = lat + (angular * 180) / Math.PI;
    const deltaLon = Math.asin(Math.sin(angular) / Math.cos(latRadians));
    if (!Number.isFinite(deltaLon)) {
      return [[minLat, lon - 0.01], [maxLat, lon + 0.01]];
    }
    const minLon = lon - (deltaLon * 180) / Math.PI;
    const maxLon = lon + (deltaLon * 180) / Math.PI;
    return [[minLat, minLon], [maxLat, maxLon]];
  }, [center, initialRadiusMeters]);

  useEffect(() => {
    if (!expanded) return;
    const map = fullscreenMapRef.current;
    if (!map) return;
    const id = window.setTimeout(() => {
      map.invalidateSize({ animate: true });
      if (computedBounds) {
        map.fitBounds(computedBounds, { padding: [56, 56] });
      }
    }, 280);
    return () => window.clearTimeout(id);
  }, [expanded, computedBounds]);

  // Calculate marker sizes based on zoom level
  const markerSizes = useMemo(() => {
    // At zoom 15: normal size
    // At zoom 18+: smaller size for less overlap
    const zoomFactor = Math.max(0.5, Math.min(1.2, 1 - (currentZoom - 15) * 0.12));
    return {
      primary: Math.round(48 * zoomFactor),
      card: Math.round(34 * zoomFactor),
      other: Math.round(28 * zoomFactor),
    };
  }, [currentZoom]);

  const primaryIcon = useMemo(
    () => buildIcon({ image: primaryImageUrl, size: markerSizes.primary, ringColor: '#FB7A4A', fallbackLetter: primaryName, highlight: true }),
    [primaryImageUrl, primaryName, markerSizes.primary]
  );

  // Combine original spots with dynamic POIs
  const allSpots = useMemo(() => {
    // If we have a searched location, prioritize dynamic POIs
    if (searchedLocation) {
      return dynamicPOIs;
    }
    // Otherwise, combine both (deduplicate by id)
    const combined = [...spots];
    const existingIds = new Set(spots.map(s => s.id));
    dynamicPOIs.forEach(poi => {
      if (!existingIds.has(poi.id)) {
        combined.push(poi);
      }
    });
    return combined;
  }, [spots, dynamicPOIs, searchedLocation]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allSpots.filter((s) => {
      const cat = normalizeCategory(s.category);
      const inFilter = selected.has(cat) || (cat === 'other' && Array.from(selected).every(k => k !== 'poi' && k !== 'restaurant' && k !== 'shopping' && k !== 'lodging'));
      if (!inFilter) return false;
      if (!q) return true;
      const text = `${s.name ?? ''} ${s.address ?? ''} ${s.description ?? ''}`.toLowerCase();
      return text.includes(q);
    });
  }, [allSpots, selected, query]);

  const cardSpots = useMemo(() => filtered.filter((spot) => spot.source === 'card'), [filtered]);
  const otherSpots = useMemo(() => filtered.filter((spot) => spot.source !== 'card'), [filtered]);

  // Calculate total distance for route
  const totalDistance = useMemo(() => {
    if (cardSpots.length < 2) return null;
    let total = 0;
    for (let i = 1; i < cardSpots.length; i++) {
      const prev = cardSpots[i - 1];
      const curr = cardSpots[i];
      total += calculateDistance(prev.lat, prev.lng, curr.lat, curr.lng);
    }
    return formatDistance(total);
  }, [cardSpots]);

  // Calculate bounds for filtered results
  const filteredBounds = useMemo<LatLngBoundsExpression | null>(() => {
    if (filtered.length === 0) return null;
    
    let minLat = center[0];
    let maxLat = center[0];
    let minLng = center[1];
    let maxLng = center[1];
    
    filtered.forEach(spot => {
      minLat = Math.min(minLat, spot.lat);
      maxLat = Math.max(maxLat, spot.lat);
      minLng = Math.min(minLng, spot.lng);
      maxLng = Math.max(maxLng, spot.lng);
    });
    
    // Add padding to bounds
    const latPadding = (maxLat - minLat) * 0.1 || 0.005;
    const lngPadding = (maxLng - minLng) * 0.1 || 0.005;
    
    return [
      [minLat - latPadding, minLng - lngPadding],
      [maxLat + latPadding, maxLng + lngPadding]
    ];
  }, [filtered, center]);



  // Clustering disabled: react-leaflet-cluster conflicts with react-leaflet v5. Render markers directly for now.

  const renderMap = (isExpandedView: boolean, targetRef: React.MutableRefObject<L.Map | null>) => (
    <MapContainer
      key={isExpandedView ? 'nearby-map-expanded' : 'nearby-map-inline'}
      center={normalizedCenter}
      zoom={hasSpots ? 15 : 14}
      bounds={computedBounds || undefined}
      style={{ height: isExpandedView ? '100%' : height, width: '100%' }}
      scrollWheelZoom={isExpandedView}
      zoomControl={false}
      maxZoom={22}
      minZoom={3}
      whenCreated={(map) => {
        targetRef.current = map;
        if (computedBounds) {
          map.fitBounds(computedBounds, { padding: [isExpandedView ? 48 : 32, isExpandedView ? 48 : 32] });
        }
        // Add zoom control at bottom-left to avoid overlap with search box / chips.
        if (!embedded) {
          L.control.zoom({ position: 'bottomleft' }).addTo(map);
        }
      }}
    >
      <RecenterOnChange center={normalizedCenter} bounds={computedBounds} searchedLocation={searchedLocation} />
      <MapMoveHandler onMoveEnd={handleMapMoveEnd} onZoomEnd={handleZoomEnd} />
      <TileLayer 
        attribution="© OpenStreetMap contributors" 
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        maxZoom={22}
        maxNativeZoom={19}
      />
      {/* Top controls: search + filter chips. Only show in expanded view or wide screens. */}
      {showControls && (isExpandedView || viewportWidth >= 640) && (
        <div
          style={{
            position: 'absolute',
            top: isExpandedView ? 18 : 12,
            left: isExpandedView ? 18 : 12,
            right: isExpandedView ? 18 : 100,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            zIndex: 1000,
            pointerEvents: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 600, position: 'relative' }}>
            {searchedLocation && (
              <button
                type="button"
                onClick={() => {
                  setSearchedLocation(null);
                  setCurrentMapCenter(center);
                  fetchNearbyPOIs(center[0], center[1], initialRadiusMeters || 1000);
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: 9999,
                  border: '1px solid rgba(0,0,0,0.08)',
                  background: 'rgba(255,255,255,0.95)',
                  boxShadow: '0 6px 14px rgba(0,0,0,0.15)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#0F172A',
                  whiteSpace: 'nowrap',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.95)';
                }}
                aria-label="返回原位置"
              >
                <PlatformIcon name="my-location" size={16} color="#0B61D6" family="MaterialIcons" />
                返回
              </button>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 12px',
                borderRadius: 9999,
                background: 'rgba(255,255,255,0.95)',
                boxShadow: '0 6px 14px rgba(0,0,0,0.15)',
                border: '1px solid rgba(0,0,0,0.08)',
                flex: 1,
                minWidth: 220,
                touchAction: 'pan-x',
              }}
            >
              <PlatformIcon name="search" size={18} color="#0F172A" family="MaterialIcons" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowSuggestions(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchResults.length > 0) {
                    e.preventDefault();
                    handleSearchResultClick(searchResults[0]);
                  }
                }}
                placeholder="搜索任意地点…"
                style={{
                  marginLeft: 8,
                  outline: 'none',
                  border: 'none',
                  background: 'transparent',
                  width: '100%',
                  fontSize: 13,
                  color: '#0F172A',
                }}
              />
              {searching && (
                <div
                  style={{
                    marginLeft: 8,
                    width: 16,
                    height: 16,
                    border: '2px solid #E5E7EB',
                    borderTopColor: '#0B61D6',
                    borderRadius: '50%',
                    animation: 'spin 0.6s linear infinite',
                  }}
                />
              )}
              {!searching && query.trim() && filtered.length > 0 && (
                <div
                  style={{
                    marginLeft: 8,
                    padding: '2px 8px',
                    borderRadius: 9999,
                    background: '#10B981',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {filtered.length}个
                </div>
              )}
              {query.trim() && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    setShowSuggestions(false);
                  }}
                  style={{
                    marginLeft: 4,
                    padding: 4,
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  aria-label="清除搜索"
                >
                  <PlatformIcon name="close" size={16} color="#64748B" family="MaterialIcons" />
                </button>
              )}
            </div>
            {/* Search suggestions dropdown */}
            {showSuggestions && searchResults.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: 8,
                  background: 'rgba(255,255,255,0.98)',
                  borderRadius: 12,
                  boxShadow: '0 12px 28px rgba(0,0,0,0.25)',
                  border: '1px solid rgba(0,0,0,0.1)',
                  maxHeight: 320,
                  overflowY: 'auto',
                  zIndex: 2000,
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {searchResults.map((result, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSearchResultClick(result)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: 'none',
                      borderBottom: idx < searchResults.length - 1 ? '1px solid rgba(0,0,0,0.06)' : 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(15,23,42,0.04)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <PlatformIcon name="place" size={18} color="#0B61D6" family="MaterialIcons" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#0F172A', marginBottom: 2 }}>
                        {result.name || result.display_name.split(',')[0]}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748B', lineHeight: 1.4 }}>
                        {result.display_name}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflowX: 'auto', paddingBottom: 2 }}>
            {/* Loading indicator */}
            {loadingPOIs && (
              <div
                style={{
                  padding: '6px 12px',
                  borderRadius: 9999,
                  background: 'rgba(255,255,255,0.95)',
                  border: '1px solid rgba(0,0,0,0.08)',
                  boxShadow: '0 6px 14px rgba(0,0,0,0.12)',
                  fontSize: 11,
                  color: '#64748B',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  whiteSpace: 'nowrap',
                }}
              >
                <div
                  style={{
                    width: 12,
                    height: 12,
                    border: '2px solid #E5E7EB',
                    borderTopColor: '#0B61D6',
                    borderRadius: '50%',
                    animation: 'spin 0.6s linear infinite',
                  }}
                />
                加载中...
              </div>
            )}
            {/* All toggle */}
            <button
              type="button"
              onClick={() => setSelected(new Set<FilterKey>(['poi','restaurant','shopping','lodging']))}
              style={{
                whiteSpace: 'nowrap',
                padding: '6px 10px',
                borderRadius: 9999,
                border: '1px solid rgba(0,0,0,0.08)',
                background: selected.size === 4 ? '#0F172A' : 'rgba(255,255,255,0.95)',
                color: selected.size === 4 ? '#fff' : '#0F172A',
                boxShadow: '0 6px 14px rgba(0,0,0,0.12)',
                fontSize: 12,
                cursor: 'pointer',
                touchAction: 'pan-x',
              }}
            >全部</button>
            {FILTERS.map((f) => {
              const enabled = selected.has(f.key);
              const visible = availableFilters.has(f.key);
              if (!visible) return null;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => {
                    const next = new Set(selected);
                    if (enabled) next.delete(f.key); else next.add(f.key);
                    // 不允许全关：至少保留一个
                    if (next.size === 0) next.add(f.key);
                    setSelected(next);
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    whiteSpace: 'nowrap',
                    padding: '6px 10px',
                    borderRadius: 9999,
                    border: '1px solid rgba(0,0,0,0.08)',
                    background: enabled ? '#0F172A' : 'rgba(255,255,255,0.95)',
                    color: enabled ? '#fff' : '#0F172A',
                    boxShadow: '0 6px 14px rgba(0,0,0,0.12)',
                    fontSize: 12,
                    cursor: 'pointer',
                    touchAction: 'pan-x',
                  }}
                  aria-pressed={enabled}
                >
                  <PlatformIcon name={f.icon.name as any} size={14} color={enabled ? '#fff' : '#0F172A'} family={f.icon.family || 'MaterialIcons'} />
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <Marker position={normalizedCenter} icon={primaryIcon}>
        {primaryLabel ? <Popup>{primaryLabel}</Popup> : null}
        {(primaryLabel || primaryAddress) ? (
          <Tooltip direction="top" offset={[0, -24]} opacity={0.95} permanent={false} sticky>
            <div
              style={{
                maxWidth: isExpandedView ? 420 : 240,
                minWidth: isExpandedView ? 220 : 180,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: 1.45,
              }}
            >
              {primaryLabel ? <strong>{primaryLabel}</strong> : null}
              {primaryAddress ? <div style={{ marginTop: primaryLabel ? 6 : 0 }}>{primaryAddress}</div> : null}
            </div>
          </Tooltip>
        ) : null}
      </Marker>
      {/* Draw distance line from primary spot to selected spot only */}
      {selectedSpotId && (() => {
        const selectedSpot = allSpots.find(s => s.id === selectedSpotId);
        if (!selectedSpot) return null;
        const distance = calculateDistance(center[0], center[1], selectedSpot.lat, selectedSpot.lng);
        const distanceText = formatDistance(distance);
        const midLat = (center[0] + selectedSpot.lat) / 2;
        const midLng = (center[1] + selectedSpot.lng) / 2;
        return (
          <>
            <Polyline
              positions={[center, [selectedSpot.lat, selectedSpot.lng]]}
              pathOptions={{
                color: '#F97316',
                weight: 3,
                opacity: 0.85,
                dashArray: '8, 6',
              }}
            />
            <Marker
              position={[midLat, midLng]}
              icon={L.divIcon({
                className: '',
                html: `
                  <div style="
                    background: rgba(249, 115, 22, 0.95);
                    border: 2px solid #fff;
                    border-radius: 16px;
                    padding: 4px 12px;
                    font-size: 13px;
                    font-weight: 700;
                    color: #fff;
                    box-shadow: 0 3px 10px rgba(249, 115, 22, 0.4);
                    white-space: nowrap;
                    pointer-events: none;
                    position: absolute;
                    left: 50%;
                    top: 50%;
                    transform: translate(-50%, -50%);
                  ">
                    ${distanceText}
                  </div>
                `,
                iconSize: [1, 1],
                iconAnchor: [0.5, 0.5],
              })}
              interactive={false}
            />
          </>
        );
      })()}
      {cardSpots.map((spot, idx) => {
        const icon = buildIcon({ 
          image: spot.imageUrl, 
          size: markerSizes.card, 
          ringColor: '#F97316', 
          fallbackLetter: spot.name,
          iconName: spot.imageUrl ? undefined : getCategoryIcon(spot.category),
          category: spot.category
        });
        
        // 计算到上一个点的距离（cardSpots 在 filtered 中的实际索引）
        let distanceFromPrevious: string | null = null;
        const spotIndexInFiltered = filtered.findIndex(s => s.id === spot.id);
        if (spotIndexInFiltered > 0) {
          const prevSpot = filtered[spotIndexInFiltered - 1];
          const distance = calculateDistance(prevSpot.lat, prevSpot.lng, spot.lat, spot.lng);
          distanceFromPrevious = formatDistance(distance);
        }
        
        return (
          <Marker
            key={spot.id}
            position={[spot.lat, spot.lng]}
            icon={icon}
            eventHandlers={{
              click: () => {
                // First click selects, second click opens detail page
                if (selectedSpotId === spot.id) {
                  // Already selected - open detail page
                  onSelectSpot && onSelectSpot(spot.id);
                } else {
                  // First click - select to show distance line
                  setSelectedSpotId(spot.id);
                }
              },
            }}
          >
            <Popup>
              <div style={{ minWidth: 180, maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.45 }}>
                <strong>{spot.name || '景点'}</strong>
                {spot.address ? <div style={{ marginTop: 6 }}>{spot.address}</div> : null}
                {!spot.address && spot.description ? <div style={{ marginTop: 6 }}>{spot.description}</div> : null}
              </div>
            </Popup>
            <Tooltip direction="top" offset={[0, -18]} opacity={0.95} sticky>
              <div style={{ maxWidth: isExpandedView ? 360 : 230, minWidth: isExpandedView ? 200 : 160, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.45 }}>
                <strong>{spot.name || '景点'}</strong>
                {spot.address ? <div style={{ marginTop: 4 }}>{spot.address}</div> : null}
                {!spot.address && spot.description ? <div style={{ marginTop: 4 }}>{spot.description}</div> : null}
              </div>
            </Tooltip>
          </Marker>
        );
      })}
      {otherSpots.map((spot) => {
        const icon = buildIcon({ 
          image: spot.imageUrl, 
          size: markerSizes.other, 
          ringColor: '#2563EB', 
          fallbackLetter: spot.name,
          iconName: spot.imageUrl ? undefined : getCategoryIcon(spot.category),
          category: spot.category
        });
        return (
          <Marker
            key={spot.id}
            position={[spot.lat, spot.lng]}
            icon={icon}
            eventHandlers={{
              click: (e) => {
                // Toggle selection to show/hide distance line
                setSelectedSpotId(prev => prev === spot.id ? null : spot.id);
                // Prevent default map behavior
                if (e.originalEvent) {
                  e.originalEvent.stopPropagation();
                }
              },
            }}
          >
            <Popup>
              <div style={{ minWidth: 170, maxWidth: 300, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.45 }}>
                <strong>{spot.name || '地点'}</strong>
                {spot.address ? <div style={{ marginTop: 6 }}>{spot.address}</div> : null}
                {!spot.address && spot.description ? <div style={{ marginTop: 6 }}>{spot.description}</div> : null}
              </div>
            </Popup>
            <Tooltip direction="top" offset={[0, -16]} opacity={0.95} sticky>
              <div style={{ maxWidth: isExpandedView ? 320 : 220, minWidth: isExpandedView ? 180 : 150, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.45 }}>
                <strong>{spot.name || '地点'}</strong>
                {spot.address ? <div style={{ marginTop: 4 }}>{spot.address}</div> : null}
                {!spot.address && spot.description ? <div style={{ marginTop: 4 }}>{spot.description}</div> : null}
              </div>
            </Tooltip>
          </Marker>
        );
      })}
      {/* Searched location marker */}
      {searchedLocation && (
        <Marker
          position={[searchedLocation.lat, searchedLocation.lon]}
          icon={buildIcon({ 
            size: Math.round(40 * Math.max(0.5, Math.min(1.2, 1 - (currentZoom - 15) * 0.12))), 
            ringColor: '#8B5CF6', 
            fallbackLetter: searchedLocation.name, 
            highlight: false,
            iconName: 'place'
          })}
          eventHandlers={{
            click: () => {
              // Convert searched location to NearbySpot format and call onCreateCard
              const spot: NearbySpot = {
                id: `search-${searchedLocation.lat}-${searchedLocation.lon}`,
                name: searchedLocation.name || '',
                lat: searchedLocation.lat,
                lng: searchedLocation.lon,
                address: searchedLocation.address || '',
                source: 'osm',
                category: 'poi',
              };
              if (onCreateCard) {
                onCreateCard(spot);
              }
            }
          }}
        >
          <Popup>
            <div style={{ minWidth: 180, maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.45 }}>
              <strong>{searchedLocation.name}</strong>
              {searchedLocation.address && <div style={{ marginTop: 6, fontSize: 12, color: '#64748B' }}>{searchedLocation.address}</div>}
              <button
                onClick={() => {
                  const spot: NearbySpot = {
                    id: `search-${searchedLocation.lat}-${searchedLocation.lon}`,
                    name: searchedLocation.name || '',
                    lat: searchedLocation.lat,
                    lng: searchedLocation.lon,
                    address: searchedLocation.address || '',
                    source: 'osm',
                    category: 'poi',
                  };
                  if (onCreateCard) {
                    onCreateCard(spot);
                  }
                }}
                style={{
                  marginTop: 8,
                  padding: '6px 12px',
                  backgroundColor: '#0B61D6',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                創建卡片
              </button>
            </div>
          </Popup>
          <Tooltip direction="top" offset={[0, -20]} opacity={0.95} permanent={false} sticky>
            <div style={{ maxWidth: isExpandedView ? 360 : 230, minWidth: isExpandedView ? 200 : 160, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.45 }}>
              <strong>{searchedLocation.name}</strong>
              {searchedLocation.address && <div style={{ marginTop: 4, fontSize: 11, color: '#64748B' }}>{searchedLocation.address}</div>}
            </div>
          </Tooltip>
        </Marker>
      )}
    </MapContainer>
  );

  return (
    <>
      <div
        style={{
          position: 'relative',
          borderRadius: embedded ? 0 : 12,
          overflow: 'hidden',
          boxShadow: embedded ? 'none' : '0 12px 26px rgba(15,23,42,0.18)',
          pointerEvents: expanded ? 'none' : 'auto',
          opacity: expanded ? 0.35 : 1,
          transition: 'opacity 0.18s ease',
          height: height,
          width: '100%',
        }}
      >
        {renderMap(false, inlineMapRef)}
        {/* showTotal prop controls whether total distance appears in fullscreen header; default false for inline use */}
        {/* Inline total-distance badge intentionally hidden on small map; shown only in fullscreen header */}
        {showFullscreenButton && (
        <button
          type="button"
          aria-label="全屏查看地图"
          onClick={toggleExpanded}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 40,
            height: 40,
            borderRadius: 9999,
            border: 'none',
            background: 'rgba(15,23,42,0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 12px 24px rgba(15,23,42,0.28)',
            color: '#fff',
            transition: 'transform 0.18s ease, background 0.18s ease',
            zIndex: 999,
          }}
          onMouseEnter={(event) => {
            event.currentTarget.style.transform = 'scale(1.05)';
            event.currentTarget.style.background = 'rgba(15,23,42,0.98)';
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.transform = 'scale(1)';
            event.currentTarget.style.background = 'rgba(15,23,42,0.92)';
          }}
        >
          <PlatformIcon name="fullscreen" size={20} color="#fff" family="MaterialIcons" />
        </button>
        )}
      </div>
      <FullscreenWrapper
        visible={expanded}
        onClose={closeExpanded}
        maxWidth="100%"
        contentStyle={{
          // Make the portal content itself be a true full-viewport overlay container.
          // Override the default card look from FullscreenWrapper.webContent.
          backgroundColor: '#0F172A',
          boxShadow: 'none',
          padding: 0,
          width: '100%',
          maxWidth: '100%',
          height: '100vh',
          maxHeight: '100vh',
          borderRadius: 0,
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '100%',
            margin: 0,
            background: '#0F172A',
            color: '#fff',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            maxHeight: '100vh',
            overflow: 'hidden',
            boxShadow: 'none',
            transform: sheetActive ? 'translateY(0)' : 'translateY(56px)',
            opacity: sheetActive ? 1 : 0,
            transition: 'transform 0.28s ease, opacity 0.28s ease',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              padding: '20px 24px 12px',
              background: 'linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(15,23,42,0.65) 100%)',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              minHeight: 84,
              gap: 16,
            }}
          >
            {(() => { try { console.log('[NearbyMap] fullscreen header render', { primaryLabel, primaryAddress, searchedLocation }); } catch {} return null; })()}
            {(() => {
              // Prefer searchedLocation name/address for the fullscreen header when present
              const headerTitle = (searchedLocation && searchedLocation.name) || primaryLabel || '';
              const headerAddress = (searchedLocation && searchedLocation.address) || primaryAddress || '';
              return (
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    {headerTitle ? (
                      <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>{headerTitle}</div>
                    ) : null}
                    {(showTotal && totalDistance) && (
                      <div
                        style={{
                          padding: '6px 12px',
                          borderRadius: 9999,
                          background: 'rgba(251,122,74,0.95)',
                          border: '1px solid rgba(249,115,22,0.3)',
                          boxShadow: '0 4px 12px rgba(249,115,22,0.3)',
                          fontSize: 12,
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          whiteSpace: 'nowrap',
                          fontWeight: 600,
                        }}
                      >
                        <span>🚶</span>
                        <span>总路程 {totalDistance}</span>
                      </div>
                    )}
                  </div>
                  {headerAddress ? (
                    <div style={{ marginTop: 6, fontSize: 14, lineHeight: 1.5, color: 'rgba(255,255,255,0.72)' }}>{headerAddress}</div>
                  ) : null}
                </div>
              );
            })()}
            <button
              type="button"
              aria-label="关闭全屏地图"
              onClick={closeExpanded}
              style={{
                width: 42,
                height: 42,
                borderRadius: 9999,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(15,23,42,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#fff',
                transition: 'background 0.2s ease, transform 0.2s ease',
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.transform = 'scale(1.05)';
                event.currentTarget.style.background = 'rgba(15,23,42,0.85)';
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.transform = 'scale(1)';
                event.currentTarget.style.background = 'rgba(15,23,42,0.6)';
              }}
            >
              <PlatformIcon name="fullscreen-exit" size={22} color="#fff" family="MaterialIcons" />
            </button>
          </div>
          <div style={{ flex: 1, position: 'relative', background: '#020617' }}>
            {renderMap(true, fullscreenMapRef)}
          </div>
        </div>
      </FullscreenWrapper>
    </>
  );
};

export default NearbyMap;
